import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { scrollIntoView } from 'utils';

import cn from "classnames";
import css from './SliderListBlock.module.scss';
import Icon from 'components/Icon';

class SliderListBlock extends React.Component {
    static propTypes = {
        data: PropTypes.array,
        size: PropTypes.oneOf([
            'large', 'small'
        ]).isRequired,
        callback: PropTypes.func,
        Component: PropTypes.func,
        className: PropTypes.string,
        selector: PropTypes.string.isRequired,
        theme: PropTypes.oneOfType([PropTypes.number,PropTypes.string]),
        mode: PropTypes.oneOf([
            'default', 'horizontal-list'
        ]),
        duration: PropTypes.number
    }

    static defaultProps = {
        size: 'large',
        selector: '[data-active="true"]',
        mode: 'default',
        duration: 400
    }

    state = {
        active: 0,
        forward: true,
        loaded: false,
    }

    componentDidMount() {
        // Don't add event listener for an empty imageblock
        if(this.props.data.length > 0) {
            (typeof window !== 'undefined') && window.addEventListener("resize", this._postSetState);

            // DOMContentLoaded event should fix onLoad event not triggering
            // for server side rendered images (that are cached)
            (typeof window !== 'undefined') && window.addEventListener("DOMContentLoaded", this._postSetState);

            this._postSetState();
        }
        this.slideDurationSetting = this.props.duration;
        this.scrollLeft = 0;
        this.sliding = false;
    }

    componentDidUpdate(){
        this.props.callback && this.props.callback.apply(null, [this.props.data[this.state.active]]);
    }

    _handleIndicatorClick = e => {
        e.preventDefault();
        const index = parseInt(e.currentTarget.dataset.index),
            forward = index > this.state.active;

        if (this.state.active === index) return;

        this.setState({ active: index, forward: forward }, this._postSetState);
    }

    _handleNavigationClick = e => {
        e.preventDefault();
        const { direction } = e.currentTarget.dataset;

        if (direction === "next") {
            if (this.props.data.length <= this.state.active + 1) return;
            this.setState({ active: this.state.active + 1, forward: true }, this._postSetState);
        }

        else if (direction === "prev") {
            if ((this.state.active - 1) < 0) return;
            this.setState({ active: this.state.active - 1, forward: false }, this._postSetState);
        }
    }

    _dataLoaded = e => {
        const image = e.currentTarget;

        if (image.dataset.active === "true") {
            this.setState({ height: image.height, loaded: true });
        }
    }

    _postSetState = throttle(() => {
        if (this.block) {
            const element = this.block.querySelector(this.props.selector);

            if(element) {
                this.setState({ height: element.clientHeight });
            }

            // // scroll to active element
            this._debouncedSlideTo();
        }
    }, 100);

    _debouncedSlideTo = debounce(() => {
        if(scrollIntoView(
            this.wrapper,
            this.wrapper.firstChild.childNodes[this.state.active],
            this.slideDurationSetting,
            this.slideTimeout,
            false
        )){
            this.sliding = true;
        }
    }, 250);

    slideTimeout = () => {
        setTimeout(() => {
            if(this.sliding){
                this.sliding = false;
                cancelAnimationFrame(window.__scrollIntoViewID);
            }
        },50);
    }

    _moveStart = e => {
        if(this.props.mode === 'default' || this.sliding){
            return;
        }
        this._x = parseFloat(e.clientX || e.touches[0].clientX).toFixed(2);
    }

    _move = e => {
        if(this.props.mode === 'default' || this.sliding){
            return;
        }
        let currX = parseFloat(e.clientX || e.touches[0].clientX).toFixed(2);
        this.direction = currX - this._x > 0 ? -1 : 1;
    }

    _moveEnd = () => {
        if(this.props.mode === 'default' || this.sliding){
            return;
        }
        let newActive = Math.max(0, Math.min(this.props.data.length - 1, this.state.active + this.direction));
        this.setState({
            active: newActive,
        }, this._debouncedSlideTo);
    }

    render() {
        const {
            data,
            size,
            Component,
            className,
            mode,
            ...rest
        } = this.props;

        const {
            active,
            loaded,
            forward
        } = this.state;

        if (! data || data.length === 0) return null;

        const carousel = data.length > 1;

        return (
            <div
                className={cn("slider-list-block", css.container, className, {
                    [css.carousel]: carousel,
                    [css.condensed]: size === 'small',
                    [css["theme-"+rest.theme]]: true,
                    [css['mode-'+mode]]: true
                })}
                ref={node => this.block = node}
            >
                <div
                    className={css["slide-wrapper"]}
                    ref={node => this.wrapper = node}
                    onScroll={this._handleScroll}
                    onTouchStart={this._moveStart}
                    onTouchMove={this._move}
                    onTouchEnd={this._moveEnd}
                    onMouseDown={this._moveStart}
                    onMouseMove={this._move}
                    onMouseUp={this._moveEnd}
                >
                    <ul className={css.slides}>
                        {data.map((item, index) => {
                            return (
                                <li
                                    className={active === index ? cn("active", css.active) : null}
                                    data-active={active === index}
                                    key={index}
                                >
                                    <Component
                                        active={active === index}
                                        key={index}
                                        ref={node => this.activeNode = node}
                                        onLoad={this._dataLoaded}
                                        size={size}
                                        {...rest}
                                        {...item}
                                    />
                                </li>
                            );
                        })}
                        {mode==='horizontal-list' &&
                            <li className={css['last-hidden-list-item']}></li>
                        }
                    </ul>
                </div>
                {loaded && (
                    <div
                        className={css.shadow}
                        style={{ transform: `translateY(calc(${this.state.height}px - 100% - 10px))` }}
                    />
                )}
                {data.length > 1 && [
                    <div
                        className={cn("nav", css.nav)}
                        key="nav"
                        style={{ transform: `translateY(${this.state.height / 2}px)` }}
                    >
                        {!active == 0 && (
                            <button
                                type="button"
                                data-direction="prev"
                                onClick={this._handleNavigationClick}
                                className={cn("nav-prev", css["nav-prev"])}
                            >
                                <Icon icon="arrow-grey-right" />
                                <span>Previous</span>
                            </button>
                        )}
                        {!(active == (data.length - 1)) && (
                            <button
                                type="button"
                                data-direction="next"
                                onClick={this._handleNavigationClick}
                                className={cn("nav-next", css["nav-next"])}
                            >
                                <Icon icon="arrow-grey-left" />
                                <span>Next</span>
                            </button>
                        )}
                    </div>,
                    <ul
                        className={cn("nav-indicators", css["nav-indicators"])}
                        key="indicators"
                        style={{ transform: `translateY(${this.state.height}px)` }}
                    >
                        {data.map((image, index) => (
                            <li key={index}>
                                <button
                                    data-index={index}
                                    type="button"
                                    onClick={this._handleIndicatorClick}
                                    className={`
                                        ${active === index ? cn("active", css.active) : null}
                                        ${forward ? css["direction-forward"] : null}
                                    `}
                                >
                                    <span>{index+1}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                ]}
            </div>
        );
    }
}

export default SliderListBlock;



// WEBPACK FOOTER //
// ./src/components/SliderListBlock/SliderListBlock.jsx